import { ReactNode } from 'react';

import { mergeClasses } from '@/utils';

import { CloseIcon } from '@/assets/icons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  IButton,
} from '@/components/common';

interface IBaseModal {
  open: boolean;
  onOpen: (open: boolean) => void;
  title: string | ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;

  hideFooter?: boolean;
  hideCloseButton?: boolean;

  isOkButtonHidden?: boolean;
  okButtonLabel?: string;
  okButtonColor?: IButton['buttonColor'];
  onOk?: () => void;
  okButtonClassName?: string;
  isOkButtonDisabled?: boolean;

  isCancelButtonHidden?: boolean;
  cancelButtonText?: string;
  onCancel?: () => void;
  cancelButtonClassName?: string;
  isCancelButtonDisabled?: boolean;

  // Customization
  contentClassName?: string;

  // Behavior
  closeOnEscape?: boolean;
}

const BaseModal = ({
  open,
  onOpen,
  title,
  subtitle,
  children,
  hideFooter = false,
  hideCloseButton = false,
  isOkButtonHidden = false,
  okButtonLabel = 'Continue',
  okButtonColor = 'primary',
  onOk,
  okButtonClassName,
  isOkButtonDisabled = false,
  isCancelButtonHidden = false,
  cancelButtonText = 'Cancel',
  onCancel,
  cancelButtonClassName,
  isCancelButtonDisabled = false,
  contentClassName,
  closeOnEscape = true,
}: IBaseModal) => {
  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogContent
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          if (!closeOnEscape) e.preventDefault();
        }}
        className={mergeClasses(
          'flex flex-col rounded-2xl p-6 bg-white w-[480px]',
          contentClassName,
        )}
      >
        <DialogHeader className="w-full flex !flex-row items-start justify-between">
          <div className="flex flex-col gap-2 w-full">
            <div className="flex gap-6">
              <DialogTitle className="flex-1 text-neutral-9 !text-h4 !font-bold">
                {title}
              </DialogTitle>
              {!hideCloseButton && (
                <CloseIcon
                  className="size-6 text-neutral-6 cursor-pointer hover:text-neutral-7 transition-colors"
                  onClick={() => onOpen(false)}
                />
              )}
            </div>
            {subtitle && (
              <span className="!text-body-sm !text-neutral-7 !font-medium">{subtitle}</span>
            )}
          </div>
        </DialogHeader>

        {children && <div className="flex-1 w-full overflow-auto">{children}</div>}

        {!hideFooter && (
          <DialogFooter className="w-full gap-2 mt-2 flex !flex-row items-center justify-end">
            {!isCancelButtonHidden && (
              <Button
                buttonStyle="outline"
                buttonColor="neutral"
                disabled={isCancelButtonDisabled}
                className={mergeClasses(
                  'bg-white border border-neutral-4 rounded-base w-[104px] h-10 !text-body-sm font-semibold',
                  cancelButtonClassName,
                )}
                onClick={onCancel || (() => onOpen(false))}
              >
                {cancelButtonText}
              </Button>
            )}
            {!isOkButtonHidden && (
              <Button
                buttonStyle="filled"
                buttonColor={okButtonColor}
                disabled={isOkButtonDisabled}
                className={mergeClasses(
                  'rounded-lg w-[104px] h-10 !text-body-sm font-semibold',
                  okButtonClassName,
                )}
                onClick={onOk}
              >
                {okButtonLabel}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BaseModal;
